import { Box, CircularProgress } from '@mui/material'
import jsPDF from 'jspdf'
import { useEffect, useState } from 'react'
import JsBarcode from 'jsbarcode'
import _ from 'lodash'
import { renderToStaticMarkup } from 'react-dom/server'
import { FaPersonWalking } from 'react-icons/fa6'

// Card badge color when the template element does not supply one
const BADGE_COLOR = { r: 255, g: 146, b: 21 }

const iconPngCache = new Map()

const iconToPng = (Icon, key, size = 256) =>
  new Promise((resolve, reject) => {
    if (iconPngCache.has(key)) {
      resolve(iconPngCache.get(key))
      return
    }
    const markup = renderToStaticMarkup(<Icon size={size} />).replace(/currentColor/g, '#00000')
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      canvas.getContext('2d').drawImage(img, 0, 0, size, size)
      const png = canvas.toDataURL('image/png')
      iconPngCache.set(key, png)
      resolve(png)
    }
    img.onerror = () => reject(new Error('Failed to rasterise icon for PDF'))
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(markup)))}`
  })

const PDFViewer = ({ pdfUrl }) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
      }}
    >
      <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none' }} />
    </Box>
  )
}

const replaceVariables = async (text, data) => {
  const compiled = _.template(text)
  const updatedString = compiled(data)
  let parsedJson
  try {
    parsedJson = JSON.parse(updatedString)
  } catch (error) {
    console.error('JSON Parse Error:', error)
    return null
  }

  return parsedJson
}

export default function ReportExportPDF({ data: details, exportNow, loading, onPdfOpened }) {

  const [pdfUrl, setPdfUrl] = useState(null)

  const rotateImage = (image, angle) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    canvas.width = image.height
    canvas.height = image.width

    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((angle * Math.PI) / 180)
    ctx.drawImage(image, -image.width / 2, -image.height / 2)

    return canvas.toDataURL('image/png')
  }

  const generateBarcode = studentId => {
    if (studentId) {
      const canvas = document.createElement('canvas')
      JsBarcode(canvas, studentId, {
        format: 'CODE128',
        displayValue: false,
        fontSize: 18,
        background: "transparent",
        width: 2,
        height: 60,
      })
      return canvas
    }
  }

  const imageProcess = async url => {
    return new Promise((resolve, reject) => {
      const u = new URL(url)
      u.searchParams.set('_cb', Date.now().toString()) // cache-buster

      const xhr = new XMLHttpRequest()
      xhr.onload = function () {
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error(`Failed to load image: HTTP ${xhr.status} for ${u.toString()}`))
          return
        }
        const reader = new FileReader()
        reader.onloadend = function () {
          resolve(reader.result)
        }
        reader.readAsDataURL(xhr.response)
      }
      xhr.onerror = function () {
        console.error('[PDF] imageProcess onerror — status:', xhr.status, 'url:', u.toString())
        reject(new Error(`Failed to load image: network error for ${u.toString()}`))
      }
      xhr.open('GET', u.toString(), true)
      xhr.responseType = 'blob'
      xhr.send()
    })
  }

  const toPngDataUrl = dataUrl =>
    new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth || img.width
        canvas.height = img.naturalHeight || img.height
        canvas.getContext('2d').drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      }
      img.onerror = () => reject(new Error(`Failed to decode image for PDF`))
      img.src = dataUrl
    })

  const decodeImage = dataUrl =>
    new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error(`Failed to decode image for PDF`))
      img.src = dataUrl
    })


  const drawImageElement = (doc, img, element) => {
    const { x, y, w, h } = element
    const round = element.borderRadius === '50%' || element.borderRadius === true
    const contain = element.fit === 'contain'

    const scale = contain ? Math.min(w / img.width, h / img.height) : 1
    const dw = contain ? img.width * scale : w
    const dh = contain ? img.height * scale : h
    const dx = x + (w - dw) / 2
    const dy = y + (h - dh) / 2

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(dw * 4)
    canvas.height = Math.round(dh * 4)
    const ctx = canvas.getContext('2d')

    if (round) {
      ctx.beginPath()
      ctx.ellipse(canvas.width / 2, canvas.height / 2, canvas.width / 2, canvas.height / 2, 0, 0, Math.PI * 2)
      ctx.closePath()
      ctx.clip()
    }

    if (contain) {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    } else {
      const boxRatio = w / h
      const srcRatio = img.width / img.height
      let sx = 0
      let sy = 0
      let sw = img.width
      let sh = img.height
      if (srcRatio > boxRatio) {
        sw = img.height * boxRatio
        sx = (img.width - sw) / 2
      } else {
        sh = img.width / boxRatio
        sy = (img.height - sh) / 2
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
    }

    doc.addImage(canvas.toDataURL('image/png'), 'PNG', dx, dy, dw, dh)

    if (round && element.borderWidth) {
      doc.setDrawColor(element.borderColor || '#000000')
      doc.setLineWidth(element.borderWidth)
      doc.ellipse(dx + dw / 2, dy + dh / 2, dw / 2, dh / 2, 'S')
    }
  }

  const hexToRgb = hex => {
    if (!hex) return null

    const cleanHex = hex.replace('#', '')

    if (cleanHex.length !== 6) return null

    const r = parseInt(cleanHex.substring(0, 2), 16)
    const g = parseInt(cleanHex.substring(2, 4), 16)
    const b = parseInt(cleanHex.substring(4, 6), 16)

    return { r, g, b }
  }

  const drawBadge = (doc, element) => {
    const radius = Math.min(element.w, element.h) / 2
    const cx = element.x + element.w / 2
    const cy = element.y + element.h / 2

    const color = element.color
    if (typeof color === 'string') {
      const rgb = hexToRgb(color)
      doc.setFillColor(...(rgb ? [rgb.r, rgb.g, rgb.b] : [BADGE_COLOR.r, BADGE_COLOR.g, BADGE_COLOR.b]))
    } else if (color) {
      doc.setFillColor(color.r, color.g, color.b)
    } else {
      doc.setFillColor(BADGE_COLOR.r, BADGE_COLOR.g, BADGE_COLOR.b)
    }
    doc.circle(cx, cy, radius, 'F')

    return { cx, cy, radius }
  }

  const resolveValue = (location, student) => {
    if (location?.toString().includes('${')) {
      return student?.[location.replace(/[${}]/g, '').trim()]
    }
    return location
  }

  const generatePDF = async (template, students) => {
    const doc = new jsPDF({
      orientation: template.page.orientation.toLowerCase(),
      unit: 'mm',
      format: [template.page.height, template.page.width],
      dpi: 300,
    })

    for (let i = 0; i < students.length; i++) {
      const student = students[i]

      if (i > 0) {
        doc.addPage()
      }

      doc.setFont(template.font.name, template.font.encoding)
      doc.setFontSize(template.font.size)

      const populatedTemplate = await replaceVariables(JSON.stringify(template), student)

      const assets = await Promise.all(
        populatedTemplate.elements.map(async element => {
          try {
            if (element.type === 'image' && element?.location) {
              return await decodeImage(await toPngDataUrl(await imageProcess(element.location)))
            }
            if (element.type === 'barcode') {
              const barcodeImage = generateBarcode(element?.text)?.toDataURL('image/png')
              return barcodeImage ? rotateImage(await decodeImage(barcodeImage), 90) : null
            }
            if (element.type === 'walk_icon') {
              return await iconToPng(FaPersonWalking, 'walker')
            }
          } catch (err) {
            // One bad asset should not lose the whole batch of cards
            console.error('[PDF] asset failed, element skipped:', element.type, err)
          }
          return null
        }),
      )

      populatedTemplate.elements.forEach((element, idx) => {
        const asset = assets[idx]
        const opacity = typeof element.opacity === 'number' ? element.opacity : 1
        if (opacity < 1) doc.setGState(new doc.GState({ opacity }))

        {
          if (element.type === 'text') {
            doc.setFontSize(element.fontSize || template.font.size)
            doc.setFont(...(element.fontName || template.font.name))

            if (element.color) {
              if (typeof element.color === 'string') {
                const rgb = hexToRgb(element.color)
                if (rgb) {
                  doc.setTextColor(rgb.r, rgb.g, rgb.b)
                }
              } else {
                doc.setTextColor(element.color.r, element.color.g, element.color.b)
              }
            }

            let text = element.textUpperCase ? element.text.toUpperCase() : element.text

            const originalText = template.elements[idx]?.text ?? ''
            const isDynamic = /\$\{.+?\}/.test(originalText)
            if (isDynamic && element.w && !element.rotation) {
              const textWidth = doc.getTextWidth(text)
              if (textWidth > element.w) {
                const currentSize = element.fontSize || template.font.size
                doc.setFontSize(Math.max((currentSize * element.w) / textWidth, 6))
              }
            }

            if (element.rotation) {
              doc.text(text, element.x, element.y, {
                angle: element.rotation,
                align: element.center ? 'center' : 'left',
              })
            } else if (element.center) {
              const textWidth = doc.getTextWidth(text)
              const pageWidth = doc.internal.pageSize.width
              const centerX = (pageWidth - textWidth) / 2
              doc.text(text, centerX, element.y)
            } else {
              doc.text(text, element.x, element.y)
            }
          }

          if (element.type === 'barcode' && asset) {
            doc.addImage(asset, 'PNG', element.x, element.y, element.w, element.h)
          }

          if (element.type === 'image' && asset) {
            drawImageElement(doc, asset, element)
          }

          if (element.type === 'walk_icon' && element.location) {
            const value = resolveValue(element.location, student)
            const mode = value ? value.toString().toLowerCase() : null

            if (mode && asset) {
              try {
                const { cx, cy, radius } = drawBadge(doc, element)
                const glyph = radius * 1.1
                doc.addImage(asset, 'PNG', cx - glyph / 2, cy - glyph / 2, glyph, glyph)
              } catch (err) {
                console.error("Error drawing icon:", err);
              }
            }
          }

          if (element.type === 'bus_icon' && element.location) {
            const value = resolveValue(element.location, student)
            const letter = value ? value.toString().trim().charAt(0).toUpperCase() : null

            if (letter) {
              try {
                const { cx, cy, radius } = drawBadge(doc, element)

                doc.setFontSize(element.fontSize || radius * 4.4)
                doc.setFont(...(element.fontName || template.font.name))
                doc.setTextColor(0, 0, 0)
                doc.text(letter, cx, cy, { align: 'center', baseline: 'middle' })

                doc.setTextColor(0, 0, 0)
              } catch (err) {
                console.error("Error drawing transport letter:", err);
              }
            }
          }

          if (element.type === 'rect') {
            const { x, y, w, h, color } = element

            if (typeof color === 'string') {
              const rgb = hexToRgb(color)
              if (rgb) {
                doc.setFillColor(rgb.r, rgb.g, rgb.b)
              }
            } else {
              doc.setFillColor(color.r, color.g, color.b)
            }

            doc.rect(x, y, w, h, 'F')
          }
        }

        if (opacity < 1) doc.setGState(new doc.GState({ opacity: 1 }))
      })
    }

    const pdfBlob = doc.output('blob')
    const pdfUrl = URL.createObjectURL(pdfBlob)

    if (exportNow) {
      window.open(pdfUrl, '_blank')
      if (onPdfOpened) onPdfOpened()
      return null
    }

    return pdfUrl
  }

  useEffect(() => {
    if (!details?.template || !details?.result?.length) return

    const buildPDF = async () => {
      const template = JSON.parse(details.template)
      const url = await generatePDF(template, details.result)

      if (!exportNow) {
        setPdfUrl(url)
      }
    }

    buildPDF()
  }, [details, exportNow])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!details || !details?.template || !details?.result[0]) {
    console.warn('[PDF] early return — missing:', { details: !!details, template: !!details?.template, result0: !!details?.result?.[0] })
    return null
  }

  return !exportNow && <Box sx={{ height: '100vh' }}>{pdfUrl ? <PDFViewer pdfUrl={pdfUrl} /> : 'Loading PDF...'}</Box>
}
